import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthorizeLoaderService {
  #platformId = inject(PLATFORM_ID);
  #loadPromise: Promise<void> | null = null;
  #isLoaded = false;

  /**
   * Loads the Authorize.net Accept.js script on-demand.
   * Returns a promise that resolves when the script is ready.
   * Multiple calls will share the same promise (singleton pattern).
   */
  loadScript(): Promise<void> {
    if (!isPlatformBrowser(this.#platformId)) {
      return Promise.resolve();
    }

    // Already loaded
    if (this.#isLoaded && window.Accept) {
      return Promise.resolve();
    }

    // Loading in progress, return existing promise
    if (this.#loadPromise) {
      return this.#loadPromise;
    }

    this.#loadPromise = new Promise((resolve, reject) => {
      // Check if script already exists
      if (document.getElementById('authorize-accept-js')) {
        this.#waitForAccept(resolve, reject);
        return;
      }

      const script = document.createElement('script');
      script.id = 'authorize-accept-js';
      // Accept.js validates the credentials against the host it was served from, so the
      // sandbox pair only works on jstest and the production pair only on js.
      script.src = environment.apis.authorize.acceptJsUrl;
      script.type = 'text/javascript';
      script.charset = 'utf-8';
      script.async = true;

      script.onload = () => {
        this.#waitForAccept(resolve, reject);
      };

      script.onerror = () => {
        this.#loadPromise = null;
        reject(new Error('Failed to load Authorize.net Accept.js'));
      };

      document.body.appendChild(script);
    });

    return this.#loadPromise;
  }

  #waitForAccept(resolve: () => void, reject: (error: Error) => void) {
    // Poll for Accept object availability
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max

    const checkAccept = () => {
      if (window.Accept) {
        // Accept.js defines window.Accept before its internal iframe
        // communication channel is fully established. Calling dispatchData()
        // immediately results in "Accept.js is not loaded correctly".
        // Allow time for the internal initialization to complete.
        setTimeout(() => {
          this.#isLoaded = true;
          resolve();
        }, 500);
      } else if (attempts >= maxAttempts) {
        this.#loadPromise = null;
        reject(new Error('Authorize.net Accept.js loaded but Accept object not available'));
      } else {
        attempts++;
        setTimeout(checkAccept, 100);
      }
    };

    checkAccept();
  }

  /**
   * Check if the script has been loaded
   */
  isLoaded(): boolean {
    return this.#isLoaded;
  }
}
